import { uid } from '../../../../lib/uid';
import type { OrgUnitOption } from '../../../human-resources/organization/organizationTypes';
import type { PositionOption } from '../../../human-resources/positions/positionTypes';
import { SURVEY_QUESTION_TYPE, type SurveyQuestionnaireQuestionInput } from '../surveyTypes';

const TITLES = [
  'Training Needs Assessment',
  'Post-Training Evaluation',
  'Learning Impact Assessment',
  'Coaching and Mentoring Feedback',
  'Leadership Development Program Evaluation',
];

/** Up to `max` distinct items, at random. */
function pickSome<T>(items: readonly T[], max: number): T[] {
  return [...items].sort(() => Math.random() - 0.5).slice(0, Math.min(max, items.length));
}

/**
 * A sample survey, so the create form fills in one click.
 *
 * **The divisions and positions are resolved against the LIVE catalogs** the form has already
 * loaded, so this takes them as arguments — a parameterless generator would fill neither. It
 * picks one or two of each, because filling the optional fields is the point; an empty catalog
 * leaves that picker on All rather than inventing an id.
 */
export function sampleSurvey(
  units: readonly OrgUnitOption[],
  positions: readonly PositionOption[],
): { title: string; orgUnitIds: string[]; positionIds: string[] } {
  const tag = uid().slice(0, 6).toUpperCase();
  const count = () => 1 + Math.floor(Math.random() * 2);

  return {
    title: `${TITLES[Math.floor(Math.random() * TITLES.length)]} ${tag}`,
    orgUnitIds: pickSome(units, count()).map((u) => u.id),
    positionIds: pickSome(positions, count()).map((p) => p.id),
  };
}

const QUESTIONNAIRE_TITLES = [
  'Training Needs Assessment',
  'Upskilling Needs Survey',
  'Learning Needs Survey',
  'Skills and Knowledge Gap Survey',
];

/**
 * A sample questionnaire, so the form fills in one click — the title suffixed so repeated clicks
 * read as different records, the optional description filled, and questions of every kind of
 * control so the editor behind each type is exercised. Ids are null: every question is new.
 *
 * **No Name and no Division question** (product call, 13 Oct 2026 — the form no longer offers
 * either type). **Every question asks what the employee needs to upskill**: the training,
 * knowledge and skills they are short of, how confident they are, and how they prefer to learn.
 */
export function sampleSurveyQuestionnaire(): {
  title: string;
  description: string;
  questions: SurveyQuestionnaireQuestionInput[];
} {
  const tag = uid().slice(0, 6).toUpperCase();
  const base = { id: null, description: null, isRequired: false, scaleMin: 1, scaleMax: 5, scaleMinLabel: null, scaleMaxLabel: null, options: [] };
  const options = (...texts: string[]) => texts.map((text) => ({ id: null, text }));

  return {
    title: `${QUESTIONNAIRE_TITLES[Math.floor(Math.random() * QUESTIONNAIRE_TITLES.length)]} ${tag}`,
    description: `Sample questionnaire ${tag}. Tell us what training, knowledge and skills would help you grow in your work.`,
    questions: [
      {
        ...base,
        text: 'Which area do you most need training in?',
        type: SURVEY_QUESTION_TYPE.MultipleChoice,
        isRequired: true,
        options: options(
          'Technical skills for my role',
          'Leadership and management',
          'Communication and writing',
          'Digital tools and systems',
          'Policies and procedures',
        ),
      },
      {
        ...base,
        text: 'Which skills would help you do your work better?',
        description: 'Choose all that apply.',
        type: SURVEY_QUESTION_TYPE.Checkbox,
        options: options(
          'Data analysis and reporting',
          'Project and time management',
          'Problem solving',
          'Presentation and facilitation',
          'Customer and stakeholder service',
        ),
      },
      {
        ...base,
        text: 'How confident are you in the skills your current role requires?',
        description: '1 is the lowest, 5 the highest.',
        type: SURVEY_QUESTION_TYPE.Scale,
        isRequired: true,
        scaleMinLabel: 'Not confident',
        scaleMaxLabel: 'Very confident',
      },
      {
        ...base,
        text: 'How do you prefer to learn?',
        type: SURVEY_QUESTION_TYPE.Dropdown,
        options: options('Classroom training', 'Online course', 'Coaching or mentoring', 'On-the-job practice'),
      },
      {
        ...base,
        text: 'What knowledge or skill gap most affects your work today?',
        type: SURVEY_QUESTION_TYPE.LongParagraph,
      },
      {
        ...base,
        text: 'Name one training you would like to attend this year.',
        type: SURVEY_QUESTION_TYPE.ShortParagraph,
      },
    ],
  };
}
